import { useState, useCallback, useRef, useEffect } from 'react';
import {
  createLinkedinAccountExternal,
  getCaptchaExternal,
  getLinkedinAccountByIdExternal,
  getThirdPartyScreenshotExternal,
  solveCaptchaExternal,
  syncLinkedinAccountExternal,
  thirdPartyClickExternal,
  setThirdPartyTextExternal,
  verifyLinkedinAccountExternal,
  updateLinkedinAccountContractExternal,
} from 'API/linkedin-accounts-external';
import { ErrorMessages, LinkedinTypes } from 'enums';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import {
  AppSyncErrors,
  AppSyncSteps,
  stepOrder,
  SyncProcessingStatuses,
} from '../app-sync-dialog/app-sync-dialog.types';
import { LinkedinAccount, LinkedinAccountStatuses, normalizeAccountStatus } from '../linkedin-accounts.types';

export interface AppSyncDialogState {
  open: boolean;
  isStarterExperience: boolean;
  loading: {
    create: boolean;
    sync: boolean;
    verifyPin: boolean;
    getCaptcha: boolean;
    solveCaptcha: boolean;
    getThirdPartyScreenshot: boolean;
    thirdPartyClick: boolean;
    update: boolean;
  };
  syncProcessingStatus: SyncProcessingStatuses;
  error: AppSyncErrors | null;
  activeStepIndex: number;
  selectedLinkedinProduct: LinkedinTypes;
  selectedCountry: OptionType;
  selectedCity: OptionType;
  userName: string;
  password: string;
  linkedinAccount: LinkedinAccount | null;
  verificationCode: string;
  captcha: string | null;
  markerPosition: { x: number; y: number; percentageX: number; percentageY: number } | null;
  fetchCaptchaIntervalId: NodeJS.Timeout | null;
  thirdPartyScreenshot: string | null;
  fetchThirdPartyScreenshotIntervalId: NodeJS.Timeout | null;
  thirdPartyText: string;
  linkedinContracts: string[];
  selectedContract: string | null;
}

const initialAppSyncState: AppSyncDialogState = {
  open: false,
  isStarterExperience: false,
  activeStepIndex: 0,
  loading: {
    create: false,
    sync: false,
    verifyPin: false,
    getCaptcha: false,
    solveCaptcha: false,
    getThirdPartyScreenshot: false,
    thirdPartyClick: false,
    update: false,
  },
  syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
  error: null,
  selectedLinkedinProduct: LinkedinTypes.LINKEDIN,
  selectedCountry: { label: '', value: '' },
  selectedCity: { label: '', value: '' },
  userName: '',
  password: '',
  linkedinAccount: null,
  verificationCode: '',
  captcha: null,
  markerPosition: null,
  fetchCaptchaIntervalId: null,
  thirdPartyScreenshot: null,
  fetchThirdPartyScreenshotIntervalId: null,
  thirdPartyText: '',
  linkedinContracts: [],
  selectedContract: null,
};

interface UseAppSyncDialogExternalProps {
  token: string | null;
  onError: (error: any, endpoint: string, method: string) => void;
  onTokenExpired: () => void;
  onLimitReached: () => void;
  onAccountAlreadyExists: (email: string) => void;
  onAccountUpdate: (account: LinkedinAccount) => void;
  onWaitBeforeSync: (waitingTime: { hours: number; minutes: number }) => void;
  onAccountAlreadySynced: () => void;
}

export function useAppSyncDialogExternal({
  token,
  onError,
  onTokenExpired,
  onLimitReached,
  onAccountAlreadyExists,
  onAccountUpdate,
  onWaitBeforeSync,
  onAccountAlreadySynced,
}: UseAppSyncDialogExternalProps) {
  const [state, setState] = useState<AppSyncDialogState>(initialAppSyncState);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
      if (state.fetchCaptchaIntervalId) clearInterval(state.fetchCaptchaIntervalId);
      if (state.fetchThirdPartyScreenshotIntervalId) clearInterval(state.fetchThirdPartyScreenshotIntervalId);
    };
  }, []);

  const clearPolling = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  // ============ STEP NAVIGATION ============

  // Die Schrittfolge war fuer Recruiter und die uebrigen Produkte identisch; die
  // Unterscheidung suggerierte einen Unterschied, den es nicht gab.
  const getStepOrder = useCallback(() => stepOrder, []);

  const increaseActiveStepIndex = useCallback(() => {
    const currentStepOrder = getStepOrder();
    setState((prev) => ({
      ...prev,
      activeStepIndex: Math.min(prev.activeStepIndex + 1, currentStepOrder.length - 1),
    }));
  }, [getStepOrder]);

  const decreaseActiveStepIndex = useCallback(() => {
    setState((prev) => ({
      ...prev,
      activeStepIndex: Math.max(prev.activeStepIndex - 1, 0),
    }));
  }, []);

  const goToSyncStep = useCallback(() => {
    const currentStepOrder = getStepOrder();
    const syncStepIndex = currentStepOrder.indexOf(AppSyncSteps.SYNCHRONIZING);
    setState((prev) => ({ ...prev, activeStepIndex: syncStepIndex }));
  }, [getStepOrder]);

  // ============ DIALOG OPEN/CLOSE ============

  const openDialog = useCallback((isStarterExperience = false) => {
    setState((prev) => ({
      ...prev,
      open: true,
      isStarterExperience,
    }));
  }, []);

  const closeDialog = useCallback(() => {
    clearPolling();
    setState(initialAppSyncState);
  }, [clearPolling]);

  // ============ FORM HANDLERS ============

  const handleLinkedinProductChange = useCallback((product: LinkedinTypes) => {
    setState((prev) => ({ ...prev, selectedLinkedinProduct: product }));
  }, []);

  const handleCountryChange = useCallback((country: OptionType) => {
    setState((prev) => ({ ...prev, selectedCountry: country }));
  }, []);

  const handleCityChange = useCallback((city: OptionType) => {
    setState((prev) => ({ ...prev, selectedCity: city }));
  }, []);

  const handleUserNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, userName: e.target.value }));
  }, []);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, password: e.target.value }));
  }, []);

  const handleVerificationCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, verificationCode: e.target.value }));
  }, []);

  const handleThirdPartyTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, thirdPartyText: e.target.value }));
  }, []);

  const handleSelectContract = useCallback((contract: string) => {
    setState((prev) => ({ ...prev, selectedContract: contract }));
  }, []);

  const handleMarkerPositionChange = useCallback((position: AppSyncDialogState['markerPosition']) => {
    setState((prev) => ({ ...prev, markerPosition: position }));
  }, []);

  // ============ STATUS HANDLING ============

  const handleAccountStatusResult = useCallback((account: LinkedinAccount): boolean => {
    clearPolling();
    onAccountUpdate(account);
    setState((prev) => ({ ...prev, linkedinAccount: account }));

    // Über welche Route der Status kam, darf hier keine Rolle spielen: die externe Route
    // liefert die rohen Backend-Werte, die Admin-Route übersetzt sie. Normalisiert wird
    // zuerst, dann entschieden.
    switch (normalizeAccountStatus(account.status)) {
      case LinkedinAccountStatuses.PENDING:
        return true; // Continue polling
      case LinkedinAccountStatuses.TWO_FA:
        setState((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.TWO_FA }));
        return false;
      case LinkedinAccountStatuses.CAPTCHA:
        setState((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.CAPTCHA }));
        fetchCaptcha(account._id);
        return false;
      case LinkedinAccountStatuses.CHOOSE_CONTRACT:
        setState((prev) => ({
          ...prev,
          syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
          linkedinContracts: account.foundContractNames || [],
        }));
        return false;
      case LinkedinAccountStatuses.THIRD_PARTY:
        setState((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY }));
        fetchThirdPartyScreenshot(account._id);
        return false;
      case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
        setState((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT }));
        return false;
      case LinkedinAccountStatuses.SYNCHRONIZED:
        increaseActiveStepIndex();
        return false;
      case LinkedinAccountStatuses.WRONG_CREDENTIALS:
        setState((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.WRONG_CREDENTIALS }));
        return false;
      case LinkedinAccountStatuses.WRONG_PIN:
        setState((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.WRONG_PIN }));
        return false;
      case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
        setState((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.TIMED_OUT_CAPTCHA }));
        return false;
      case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
        setState((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.ONBOARDING_INCOMPLETE }));
        return false;
      case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
        setState((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.TWO_FA_NOT_ENABLED }));
        return false;
      default:
        setState((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.UNKNOWN }));
        return false;
    }
  }, [clearPolling, onAccountUpdate, increaseActiveStepIndex]);

  const startPolling = useCallback((accountId: string) => {
    if (!token) return;

    const checkStatus = async () => {
      const res = await getLinkedinAccountByIdExternal(token, accountId);

      if (!res.error) {
        const shouldContinue = handleAccountStatusResult(res);
        if (shouldContinue) {
          intervalIdRef.current = setInterval(checkStatus, 1000);
        }
      } else {
        clearPolling();
        if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
          onTokenExpired();
        } else {
          onError(res.error, '/api/linkedinAccounts/:id', 'get');
        }
      }
    };

    intervalIdRef.current = setInterval(checkStatus, 1000);
  }, [token, handleAccountStatusResult, clearPolling, onTokenExpired, onError]);

  // ============ CAPTCHA ============

  const fetchCaptcha = useCallback(async (accountId: string) => {
    if (!token) return;

    setState((prev) => ({ ...prev, loading: { ...prev.loading, getCaptcha: true } }));

    const fetchAndUpdate = async () => {
      const res = await getCaptchaExternal(token, accountId);
      if (!res.error && res.base64Image) {
        setState((prev) => ({
          ...prev,
          captcha: res.base64Image,
          loading: { ...prev.loading, getCaptcha: false },
        }));
      } else if (res.error) {
        if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
          onTokenExpired();
        }
        setState((prev) => ({ ...prev, loading: { ...prev.loading, getCaptcha: false } }));
      }
    };

    await fetchAndUpdate();

    // Set up interval for captcha refresh
    const intervalId = setInterval(fetchAndUpdate, 5000);
    setState((prev) => ({ ...prev, fetchCaptchaIntervalId: intervalId }));
  }, [token, onTokenExpired]);

  const solveCaptcha = useCallback(async () => {
    if (!token || !state.linkedinAccount || !state.markerPosition) return;

    setState((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: true } }));

    // Clear captcha refresh interval
    if (state.fetchCaptchaIntervalId) {
      clearInterval(state.fetchCaptchaIntervalId);
      setState((prev) => ({ ...prev, fetchCaptchaIntervalId: null }));
    }

    const res = await solveCaptchaExternal(
      token,
      state.linkedinAccount._id,
      { x: state.markerPosition.percentageX, y: state.markerPosition.percentageY }
    );

    if (!res.error) {
      setState((prev) => ({
        ...prev,
        captcha: null,
        markerPosition: null,
        syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
      }));
      startPolling(state.linkedinAccount._id);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        onTokenExpired();
      } else {
        onError(res.error, '/api/linkedinAccounts/:id/solveCaptcha', 'put');
      }
      setState((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
    }
  }, [token, state.linkedinAccount, state.markerPosition, state.fetchCaptchaIntervalId, startPolling, onTokenExpired, onError]);

  // ============ THIRD PARTY ============

  const fetchThirdPartyScreenshot = useCallback(async (accountId: string) => {
    if (!token) return;

    setState((prev) => ({ ...prev, loading: { ...prev.loading, getThirdPartyScreenshot: true } }));

    const fetchAndUpdate = async () => {
      const res = await getThirdPartyScreenshotExternal(token, accountId);
      if (!res.error && res.base64Image) {
        setState((prev) => ({
          ...prev,
          thirdPartyScreenshot: res.base64Image,
          loading: { ...prev.loading, getThirdPartyScreenshot: false },
        }));
      } else if (res.error) {
        if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
          onTokenExpired();
        }
        setState((prev) => ({ ...prev, loading: { ...prev.loading, getThirdPartyScreenshot: false } }));
      }
    };

    await fetchAndUpdate();

    const intervalId = setInterval(fetchAndUpdate, 5000);
    setState((prev) => ({ ...prev, fetchThirdPartyScreenshotIntervalId: intervalId }));
  }, [token, onTokenExpired]);

  const handleThirdPartyClick = useCallback(async () => {
    if (!token || !state.linkedinAccount || !state.markerPosition) return;

    setState((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: true } }));

    if (state.fetchThirdPartyScreenshotIntervalId) {
      clearInterval(state.fetchThirdPartyScreenshotIntervalId);
      setState((prev) => ({ ...prev, fetchThirdPartyScreenshotIntervalId: null }));
    }

    const res = await thirdPartyClickExternal(
      token,
      state.linkedinAccount._id,
      { x: state.markerPosition.percentageX, y: state.markerPosition.percentageY }
    );

    await new Promise((resolve) => setTimeout(resolve, 3000));

    if (!res.error) {
      setState((prev) => ({
        ...prev,
        thirdPartyScreenshot: null,
        markerPosition: null,
        syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
      }));
      startPolling(state.linkedinAccount._id);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        onTokenExpired();
      } else {
        onError(res.error, '/api/linkedinAccounts/:id/thirdPartyClick', 'put');
      }
      setState((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
    }
  }, [token, state.linkedinAccount, state.markerPosition, state.fetchThirdPartyScreenshotIntervalId, startPolling, onTokenExpired, onError]);

  const handleSetThirdPartyText = useCallback(async () => {
    if (!token || !state.linkedinAccount) return;

    setState((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: true } }));

    const res = await setThirdPartyTextExternal(token, state.linkedinAccount._id, state.thirdPartyText);
    await new Promise((resolve) => setTimeout(resolve, 3000));

    if (!res.error) {
      setState((prev) => ({
        ...prev,
        thirdPartyText: '',
        syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
      }));
      startPolling(state.linkedinAccount._id);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        onTokenExpired();
      } else {
        onError(res.error, '/api/linkedinAccounts/:id/thirdPartyClick', 'put');
      }
      setState((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
    }
  }, [token, state.linkedinAccount, state.thirdPartyText, startPolling, onTokenExpired, onError]);

  // ============ ACCOUNT CREATION & SYNC ============

  const createAccount = useCallback(async () => {
    if (!token) return;

    const { userName, password, selectedLinkedinProduct, selectedCountry, selectedCity } = state;

    setState((prev) => ({ ...prev, loading: { ...prev.loading, create: true } }));

    const res = await createLinkedinAccountExternal(
      token,
      selectedCountry.value,
      selectedCity.value,
      userName,
      password,
      selectedLinkedinProduct
    );

    if (!res.error) {
      setState((prev) => ({ ...prev, linkedinAccount: res }));
      onAccountUpdate(res);
      increaseActiveStepIndex();
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        onTokenExpired();
      } else if (
        res.error.message === 'LinkedIn Account Limit reached' ||
        res.error.message === 'No remaining capacity'
      ) {
        onLimitReached();
      } else if (res.error.message === 'Account already exists!') {
        onAccountAlreadyExists(userName);
      } else {
        onError(res.error, '/api/linkedinAccounts/addLinkedInAccount', 'post');
      }
    }

    setState((prev) => ({ ...prev, loading: { ...prev.loading, create: false } }));
  }, [token, state, increaseActiveStepIndex, onAccountUpdate, onTokenExpired, onLimitReached, onAccountAlreadyExists, onError]);

  const synchronizeAccount = useCallback(async (account?: LinkedinAccount) => {
    if (!token) return;

    const linkedinAccount = account || state.linkedinAccount;
    if (!linkedinAccount) return;

    setState((prev) => ({ ...prev, loading: { ...prev.loading, sync: true } }));
    goToSyncStep();

    const res = await syncLinkedinAccountExternal(token, linkedinAccount._id);

    setState((prev) => ({ ...prev, loading: { ...prev.loading, sync: false } }));

    if (!res.error) {
      startPolling(linkedinAccount._id);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        onTokenExpired();
      } else if (res.error.message?.includes('Please wait for')) {
        const match = res.error.message.match(/(\d+)h (\d+)m/);
        if (match) {
          onWaitBeforeSync({ hours: parseInt(match[1]), minutes: parseInt(match[2]) });
        }
      } else if (res.error.message === 'Account is already synchronized') {
        onAccountAlreadySynced();
      } else {
        onError(res.error, '/api/linkedinAccounts/:id/sync', 'post');
      }
    }
  }, [token, state.linkedinAccount, goToSyncStep, startPolling, onTokenExpired, onWaitBeforeSync, onAccountAlreadySynced, onError]);

  const verifyPin = useCallback(async (mode: 'pin' | 'approved' = 'pin') => {
    if (!token || !state.linkedinAccount) return;

    setState((prev) => ({ ...prev, loading: { ...prev.loading, verifyPin: true } }));

    const res = await verifyLinkedinAccountExternal(
      token,
      state.linkedinAccount._id,
      mode === 'approved' ? { action: 'approved' } : state.verificationCode
    );

    if (!res.error) {
      setState((prev) => ({
        ...prev,
        verificationCode: '',
        syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
      }));
      startPolling(state.linkedinAccount._id);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        onTokenExpired();
      } else {
        onError(res.error, '/api/linkedinAccounts/:id/verify', 'put');
      }
      setState((prev) => ({ ...prev, loading: { ...prev.loading, verifyPin: false } }));
    }
  }, [token, state.linkedinAccount, state.verificationCode, startPolling, onTokenExpired, onError]);

  const addContract = useCallback(async () => {
    if (!token || !state.linkedinAccount || !state.selectedContract) return;

    setState((prev) => ({ ...prev, loading: { ...prev.loading, update: true } }));

    const res = await updateLinkedinAccountContractExternal(
      token,
      state.linkedinAccount._id,
      state.selectedContract
    );

    if (!res.error) {
      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, update: false },
        selectedContract: '',
        linkedinContracts: [],
        syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
      }));
      startPolling(state.linkedinAccount._id);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        onTokenExpired();
      } else {
        onError(res.error, '/api/linkedinAccounts/:id/contract', 'put');
      }
      setState((prev) => ({ ...prev, loading: { ...prev.loading, update: false } }));
    }
  }, [token, state.linkedinAccount, state.selectedContract, startPolling, onTokenExpired, onError]);

  return {
    state,
    setState,
    // Dialog controls
    openDialog,
    closeDialog,
    // Step navigation
    increaseActiveStepIndex,
    decreaseActiveStepIndex,
    getStepOrder,
    // Form handlers
    handleLinkedinProductChange,
    handleCountryChange,
    handleCityChange,
    handleUserNameChange,
    handlePasswordChange,
    handleVerificationCodeChange,
    handleThirdPartyTextChange,
    handleSelectContract,
    handleMarkerPositionChange,
    // Actions
    createAccount,
    synchronizeAccount,
    verifyPin,
    solveCaptcha,
    handleThirdPartyClick,
    handleSetThirdPartyText,
    addContract,
    // Utilities
    clearPolling,
  };
}

